/* eslint-disable unicode-bom */
import React, { useState, useEffect } from 'react';
import { apiUrl, fetchJson } from '../apiBase';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import School from '@mui/icons-material/School';
import Person from '@mui/icons-material/Person';
import TrendingUp from '@mui/icons-material/TrendingUp';
import Warning from '@mui/icons-material/Warning';
import CheckCircle from '@mui/icons-material/CheckCircle';

const StatusCard = ({ title, value, subtitle, icon: Icon, color, trend }) => {
  const theme = useTheme();
  
  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 2,
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${alpha(color, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
              color: 'white',
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700, 
            color: color,
            mb: 1,
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}
        >
          {value}
        </Typography>
        
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        
        {trend && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
            <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main, mr: 0.5 }} />
            <Typography variant="caption" color="success.main">
              {trend}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const AttendanceStatus = ({ onError, schoolId }) => {
  console.log('AttendanceStatus component initialized with schoolId:', schoolId);
  
  const [attendanceData, setAttendanceData] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const theme = useTheme();

  const loadClasses = async () => {
    if (!schoolId) {
      console.error('No schoolId provided to AttendanceStatus component');
      onError('Okul bilgisi bulunamadı');
      return;
    }
    console.log('Loading classes for school:', schoolId);
    try {
      setLoadingClasses(true);
      const response = await fetchJson(apiUrl(`/admin/classes/${schoolId}`));
      console.log('Classes API response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        const classes = data
          .map((item) => {
            if (typeof item === 'string') {
              return { id: null, name: item };
            }
            return { id: item.id, name: item.name };
          })
          .filter((cls) => cls.name)
          .sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
        setClasses(classes);
        if (classes.length > 0) {
          setSelectedClass(classes[0]);
        }
      } else {
        console.error('Failed to load classes:', response.status, response.statusText);
        onError('Şube listesi yüklenemedi');
      }
    } catch (err) {
      console.error('Error loading classes:', err);
      onError('Bağlantı hatası');
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadAttendanceData = async () => {
    console.log('Loading attendance data for class:', selectedClass, 'in school:', schoolId);
    setLoading(true);
    try {
      // Fetch attendance list for the class
      const className = selectedClass?.name;
      if (!className) {
        setAttendanceData([]);
        setStudents([]);
        setLoading(false);
        return;
      }
      const response = await fetchJson(apiUrl(`/admin/attendance-status?class_name=${encodeURIComponent(className)}&school_name=${encodeURIComponent(schoolId)}`));
      if (!response.ok) {
        console.error('Failed to load attendance data:', response.status, response.statusText);
        onError('Devamsızlık verileri yüklenemedi');
        setAttendanceData([]);
        return;
      }
      const data = await response.json();
      const all = data.attendance || [];
      setAttendanceData(all);

      // Build student list from returned attendance or via students endpoint when empty
      if (all.length > 0) {
        const uniq = Array.from(new Map(all.map(r => [r.student_id, { id: r.student_id, name: r.student_name }])).values());
        setStudents(uniq);
        if (!selectedStudent && uniq.length > 0) {
          setSelectedStudent(uniq[0].id);
        }
      } else {
        // Fallback: fetch students in the class
        const classId = selectedClass?.id;
        if (!classId) {
          setStudents([]);
          setLoading(false);
          return;
        }
        const stuRes = await fetchJson(apiUrl(`/admin/students/${encodeURIComponent(schoolId)}/by-class/${encodeURIComponent(classId)}`));
        if (stuRes.ok) {
          const stuData = await stuRes.json();
          const mapped = stuData.map(s => ({ id: s.id, name: `${s.name} ${s.surname}` }));
          setStudents(mapped);
          if (!selectedStudent && mapped.length > 0) setSelectedStudent(mapped[0].id);
        } else {
          setStudents([]);
        }
      }
    } catch (err) {
      console.error('Error loading attendance data:', err);
      onError('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const loadAttendanceStats = async () => {
    try {
      const response = await fetchJson(apiUrl(`/admin/attendance-summary?school_name=${encodeURIComponent(schoolId)}`));
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('İstatistikler yüklenemedi:', err);
    }
  };

  // Load initial data when component mounts or schoolId changes
  useEffect(() => {
  console.log('AttendanceStatus: useEffect triggered, schoolId:', schoolId);
    if (schoolId) {
      loadClasses();
      loadAttendanceStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  // Load attendance data when selected class changes
  useEffect(() => {
    if (selectedClass && schoolId) {
      setSelectedStudent('');
      loadAttendanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, schoolId]);

  const getAttendanceChip = (status) => {
    const statusMap = {
      present: { label: 'Mevcut', color: 'success' },
      absent: { label: 'Devamsız', color: 'error' },
      late: { label: 'Geç', color: 'warning' },
      excused: { label: 'Mazeretli', color: 'info' }
    };
    
    return (
      <Chip
        label={statusMap[status]?.label || status}
        color={statusMap[status]?.color || 'default'}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
        Devamsızlık Yönetimi
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatusCard
              title="Toplam Öğrenci"
              value={stats.total_students || 0}
              icon={Person}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatusCard
              title="Mevcut"
              value={stats.present_today || 0}
              icon={CheckCircle}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatusCard
              title="Devamsız"
              value={stats.absent_today || 0}
              icon={Warning}
              color={theme.palette.error.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatusCard
              title="Devamsızlık Oranı"
              value={`${stats.absence_rate || 0}%`}
              icon={School}
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>
      )}

      {/* Class Selection */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Autocomplete
          options={classes}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          value={selectedClass}
          onChange={(_, v) => setSelectedClass(v || null)}
          loading={loadingClasses}
          renderInput={(params) => (
            <TextField {...params} label="Şube Seçin" placeholder="Yazdıkça filtrelenir" InputProps={{ ...params.InputProps, endAdornment: (
              <>
                {loadingClasses ? <Skeleton variant="circular" width={16} height={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ) }} />
          )}
          sx={{ mb: 2 }}
          size="small"
          clearOnEscape
        />

        {/* Student selection */}
        {loading && (
          <Box sx={{ p: 1 }}>
            <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
          </Box>
        )}
        {!loading && students.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Öğrenci Seçin</InputLabel>
            <Select
              value={selectedStudent}
              label="Öğrenci Seçin"
              onChange={(e) => setSelectedStudent(e.target.value)}
            >
              {students.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Attendance Table: only selected student's history */}
        {!loading && attendanceData.length > 0 && selectedStudent && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Öğrenci Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Öğrenci No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceData
                  .filter(r => r.student_id === selectedStudent)
                  .map((record, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{record.student_name}</TableCell>
                    <TableCell>{record.student_no}</TableCell>
                    <TableCell>{getAttendanceChip(record.status)}</TableCell>
                    <TableCell>{new Date(record.date).toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {loading && (
          <Box sx={{ p: 2 }}>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={42} sx={{ mb: 1 }} />
            ))}
          </Box>
        )}

  {!loading && attendanceData.length === 0 && selectedClass && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Bu şube için devamsızlık kaydı bulunamadı.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceStatus;
